import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response, SelectItemProps } from '@ui/src';
import { TariffLog, TariffItem, TariffItemFee } from '../../types';
import { tariffsApi } from '../services';

export interface TariffPageState {
  tariffId?: string;
  generalInfo?: TariffItem;
  logs: {
    isLoading: boolean;
    values?: TariffLog[];
  };
  selectedFeesList: string[];
  removeFeesInProgress?: boolean;
  selectedTariff?: string;
  tariffs?: SelectItemProps[];
}

const initialState: TariffPageState = {
  generalInfo: undefined,
  logs: {
    isLoading: true,
  },
  selectedFeesList: [],
};

export const tariffPageSlice = createSlice({
  name: 'tariffPage',
  initialState,
  reducers: {
    selectFees: (state, action: PayloadAction<string[]>) => {
      state.selectedFeesList = action.payload;
    },
    getLogs: (state) => {
      state.logs.isLoading = true;
    },
    getLogsSuccess: (state, action: PayloadAction<TariffLog[]>) => {
      state.logs.isLoading = false;
      state.logs.values = action.payload;
    },
    selectNewCustomersTariff: (state, action: PayloadAction<string>) => {
      state.selectedTariff = action.payload;
    },
    clearTariffPageState: (state) => {
      state.generalInfo = undefined;
      state.logs = {
        isLoading: false,
      };
      state.tariffId = undefined;
      state.selectedFeesList = [];
      state.removeFeesInProgress = undefined;
      state.selectedTariff = undefined;
      state.tariffs = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      tariffsApi.endpoints.getTariffItem.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        let nextFees: TariffItemFee[] = [];

        if (action.payload.data.fees.length) {
          nextFees = action.payload.data.fees
            .map((item) => ({
              ...item,
              writeOffValue: '',
            }))
            .sort((a, b) => a?.name?.localeCompare(b?.name));
        }

        state.generalInfo = {
          ...action.payload.data,
          fees: nextFees,
        };
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.changeTariffStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        const nextGeneralInfo = { ...state.generalInfo };

        nextGeneralInfo.status = action.payload.data.status;

        state.generalInfo = nextGeneralInfo as TariffItem;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.toggleTariffDefault.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        const nextGeneralInfo = { ...state.generalInfo };

        nextGeneralInfo.isDefault = action.payload.data.isDefault;

        state.generalInfo = nextGeneralInfo as TariffItem;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.createTariffFee.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItemFee }>) => {
        const nextGeneralInfo = { ...state.generalInfo };

        const nextFees = ([] as TariffItemFee[]).concat(
          nextGeneralInfo.fees as TariffItemFee[],
        );

        nextFees.push({
          ...action.payload.data,
          writeOffValue: '',
        });

        nextGeneralInfo.fees = nextFees;

        state.generalInfo = nextGeneralInfo as TariffItem;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.updateTariffFee.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItemFee }>) => {
        const nextGeneralInfo = { ...state.generalInfo };

        const nextFees = nextGeneralInfo.fees?.map((item) => {
          let result = { ...item };

          if (action.payload.data.id === result.id) {
            result = { ...result, ...action.payload.data };
          }

          return result;
        });

        nextGeneralInfo.fees = nextFees;

        state.generalInfo = nextGeneralInfo as TariffItem;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.deleteTariffFee.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItemFee }>) => {
        const nextGeneralInfo = { ...state.generalInfo };

        nextGeneralInfo.fees = nextGeneralInfo.fees?.filter(
          (item) => item.id !== action.payload.data.id,
        );

        state.generalInfo = nextGeneralInfo as TariffItem;
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.updateTariff.matchFulfilled,
      (state, action: PayloadAction<{ data: TariffItem }>) => {
        let nextFees: TariffItemFee[] = [];

        if (action.payload.data.fees.length) {
          nextFees = action.payload.data.fees.map((item) => ({
            ...item,
            writeOffValue: '',
          }));
        }

        state.generalInfo = {
          ...action.payload.data,
          fees: nextFees,
        };
      },
    );
    builder.addMatcher(
      tariffsApi.endpoints.getAllTariffs.matchFulfilled,
      (state, action: PayloadAction<Response<TariffItem>>) => {
        state.tariffs = action.payload.data.edges.map((item) => ({
          name: item.node.name,
          value: item.node.id,
          description: item.node.riskScoreRangeId,
        }));
      },
    );
  },
});

export const {
  getLogs,
  getLogsSuccess,
  selectFees,
  clearTariffPageState,
  selectNewCustomersTariff,
} = tariffPageSlice.actions;

export default tariffPageSlice.reducer;
